import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { User, Bell, Lock, Clock, Mail, Phone, Save, Eye, EyeOff, ExternalLink, Utensils, AlertCircle } from 'lucide-react';
import { httpClient } from '@/api';
import { useToast } from '@/providers/ToastProvider';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { MemedIntegrationStatus } from '@/components/memed/MemedIntegrationStatus';

interface DaySchedule {
  enabled: boolean;
  start: string;
  end: string;
  morningStart?: string;
  morningEnd?: string;
  afternoonStart?: string;
  afternoonEnd?: string;
  hasLunchBreak?: boolean;
}

interface DoctorSettings {
  name: string;
  email: string;
  phone: string;
  crm: string;
  crmState?: string;
  specialty: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  availability: Record<string, DaySchedule>;
}

export function DoctorSettingsPage() {
  const { toast } = useToast();
  const { user } = useAuth();

  const [settings, setSettings] = useState<DoctorSettings>({
    name: user?.name || '',
    email: user?.email || '',
    phone: (user as any)?.phone || '',
    crm: (user as any)?.crm || '',
    crmState: (user as any)?.crmState || 'SP',
    specialty: (user as any)?.specialty || 'Clínica Geral',
    notifications: {
      email: true,
      push: true,
      sms: false,
    },
    availability: {
      monday: { enabled: true, start: '08:00', end: '18:00', morningStart: '08:00', morningEnd: '12:00', afternoonStart: '13:30', afternoonEnd: '18:00', hasLunchBreak: true },
      tuesday: { enabled: true, start: '08:00', end: '18:00', morningStart: '08:00', morningEnd: '12:00', afternoonStart: '13:30', afternoonEnd: '18:00', hasLunchBreak: true },
      wednesday: { enabled: true, start: '08:00', end: '18:00', morningStart: '08:00', morningEnd: '12:00', afternoonStart: '13:30', afternoonEnd: '18:00', hasLunchBreak: true },
      thursday: { enabled: true, start: '08:00', end: '18:00', morningStart: '08:00', morningEnd: '12:00', afternoonStart: '13:30', afternoonEnd: '18:00', hasLunchBreak: true },
      friday: { enabled: true, start: '08:00', end: '18:00', morningStart: '08:00', morningEnd: '12:00', afternoonStart: '13:30', afternoonEnd: '18:00', hasLunchBreak: true },
      saturday: { enabled: false, start: '08:00', end: '12:00', morningStart: '08:00', morningEnd: '12:00', afternoonStart: '13:30', afternoonEnd: '18:00', hasLunchBreak: false },
      sunday: { enabled: false, start: '08:00', end: '12:00', morningStart: '08:00', morningEnd: '12:00', afternoonStart: '13:30', afternoonEnd: '18:00', hasLunchBreak: false },
    },
  });

  const { data: serverSettingsData } = useQuery({
    queryKey: ['doctor-my-settings'],
    queryFn: async () => {
      const resp = await httpClient.get<any>('/doctors/me/settings');
      return resp?.data?.data || resp?.data || resp;
    },
    staleTime: 60000,
  });

  useEffect(() => {
    if (serverSettingsData) {
      setSettings((prev) => ({
        ...prev,
        name: serverSettingsData.name || user?.name || prev.name,
        email: serverSettingsData.email || user?.email || prev.email,
        phone: serverSettingsData.phone || (user as any)?.phone || prev.phone,
        crm: serverSettingsData.crm || prev.crm,
        crmState: serverSettingsData.crmState || prev.crmState,
        specialty: serverSettingsData.specialty || prev.specialty,
        notifications: serverSettingsData.notifications || prev.notifications,
        availability: serverSettingsData.availability || prev.availability,
      }));
    } else if (user) {
      setSettings((prev) => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        phone: (user as any)?.phone || prev.phone,
        crm: (user as any)?.crm || prev.crm,
        specialty: (user as any)?.specialty || prev.specialty,
      }));
    }
  }, [serverSettingsData, user]);

  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const saveMutation = useMutation({
    mutationFn: async (newSettings: DoctorSettings) => {
      await httpClient.patch('/doctors/me/settings', {
        name: newSettings.name,
        phone: newSettings.phone,
        notifications: newSettings.notifications,
        availability: newSettings.availability,
      });
    },
    onSuccess: () => {
      toast({ title: 'Configurações salvas com sucesso', variant: 'success' });
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : 'Erro ao salvar configurações';
      toast({ title: 'Erro ao salvar configurações', description: msg, variant: 'error' });
    },
  });

  const passwordMutation = useMutation({
    mutationFn: async ({ current, new: newPwd }: { current: string; new: string }) => {
      await httpClient.post('/auth/change-password', { currentPassword: current, newPassword: newPwd });
    },
    onSuccess: () => {
      toast({ title: 'Senha alterada com sucesso', variant: 'success' });
      setCurrentPassword('');
      setNewPassword('');
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : 'Erro ao alterar senha';
      toast({ title: 'Erro ao alterar senha', description: msg, variant: 'error' });
    },
  });

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const dayNames: Record<string, string> = {
    monday: 'Segunda-feira',
    tuesday: 'Terça-feira',
    wednesday: 'Quarta-feira',
    thursday: 'Quinta-feira',
    friday: 'Sexta-feira',
    saturday: 'Sábado',
    sunday: 'Domingo',
  };

  const handleDayToggle = (day: string) => {
    const cur = settings.availability[day] || { enabled: false, start: '08:00', end: '18:00' };
    setSettings({
      ...settings,
      availability: {
        ...settings.availability,
        [day]: {
          ...cur,
          enabled: !cur.enabled,
        },
      },
    });
  };

  const handleLunchToggle = (day: string) => {
    const cur = settings.availability[day];
    if (!cur) return;
    const hasLunch = !cur.hasLunchBreak;
    setSettings({
      ...settings,
      availability: {
        ...settings.availability,
        [day]: {
          ...cur,
          hasLunchBreak: hasLunch,
          morningStart: cur.morningStart || '08:00',
          morningEnd: cur.morningEnd || '12:00',
          afternoonStart: cur.afternoonStart || '13:30',
          afternoonEnd: cur.afternoonEnd || '18:00',
        },
      },
    });
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Configurações da Conta</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie suas informações profissionais, preferências e disponibilidade semanal.
          </p>
        </div>
        <Button
          onClick={() => saveMutation.mutate(settings)}
          disabled={saveMutation.isPending}
          className="flex items-center gap-2 self-start sm:self-auto rounded-xl shadow-sm"
        >
          <Save className="h-4 w-4" />
          {saveMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Card MeMed */}
          <Card className="bg-card border-border overflow-hidden shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-muted/30 border-b border-border">
              <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                Prescrição Digital & Integração MeMed
              </CardTitle>
              <Link
                to="/medico/configuracoes/memed"
                className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
              >
                <span>Avançado</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </CardHeader>
            <CardContent className="pt-5 space-y-3">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Vincule e sincronize seu cadastro médico para habilitar a emissão de receitas digitais, exames e atestados com carimbo ICP-Brasil e catálogo oficial Anvisa.
              </p>
              <MemedIntegrationStatus />
            </CardContent>
          </Card>

          {/* Dados Pessoais & Governança */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <User className="h-5 w-5 text-primary" />
                Informações Pessoais e de Contato
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">Nome Completo</label>
                  <input
                    type="text"
                    value={settings.name}
                    onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    CRM / UF <span className="text-xs text-muted-foreground font-normal">(Somente Leitura)</span>
                  </label>
                  <input
                    type="text"
                    value={settings.crm || 'Definido no cadastro'}
                    disabled
                    className="w-full px-3.5 py-2.5 border border-border bg-muted/60 text-muted-foreground rounded-xl text-sm cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    E-mail Cadastrado <span className="text-xs text-muted-foreground font-normal">(Somente Leitura)</span>
                  </label>
                  <input
                    type="email"
                    value={settings.email}
                    disabled
                    className="w-full px-3.5 py-2.5 border border-border bg-muted/60 text-muted-foreground rounded-xl text-sm cursor-not-allowed"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-muted-foreground" />
                    Para alterar e-mail de acesso ou CRM, contate o suporte administrativo.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">Telefone / WhatsApp</label>
                  <input
                    type="tel"
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    placeholder="(11) 99999-9999"
                    className="w-full px-3.5 py-2.5 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm shadow-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Horários de Atendimento com Intervalo de Almoço */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <Clock className="h-5 w-5 text-primary" />
                  Horários de Atendimento & Grade Semanal
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Defina os turnos de agendamento. Você pode configurar atendimento direto ou dividir em turnos de manhã e tarde.
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3.5">
                {days.map((day) => {
                  const schedule = settings.availability[day] || {
                    enabled: false,
                    start: '08:00',
                    end: '18:00',
                    morningStart: '08:00',
                    morningEnd: '12:00',
                    afternoonStart: '13:30',
                    afternoonEnd: '18:00',
                    hasLunchBreak: true,
                  };

                  return (
                    <div
                      key={day}
                      className={`p-3.5 rounded-xl border transition-all ${
                        schedule.enabled ? 'bg-card border-border shadow-xs' : 'bg-muted/30 border-border/50 opacity-70'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="w-40 flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            id={`day-${day}`}
                            checked={schedule.enabled}
                            onChange={() => handleDayToggle(day)}
                            className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                          />
                          <label htmlFor={`day-${day}`} className="text-sm font-semibold text-foreground cursor-pointer">
                            {dayNames[day]}
                          </label>
                        </div>

                        {schedule.enabled ? (
                          <div className="flex flex-wrap items-center gap-2.5">
                            <button
                              type="button"
                              onClick={() => handleLunchToggle(day)}
                              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${
                                schedule.hasLunchBreak
                                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'
                                  : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                              }`}
                            >
                              <Utensils className="w-3 h-3" />
                              {schedule.hasLunchBreak ? 'Com Almoço' : 'Direto'}
                            </button>

                            {schedule.hasLunchBreak ? (
                              <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground">
                                <span className="text-muted-foreground">Manhã:</span>
                                <input
                                  type="time"
                                  value={schedule.morningStart || '08:00'}
                                  onChange={(e) =>
                                    setSettings({
                                      ...settings,
                                      availability: {
                                        ...settings.availability,
                                        [day]: { ...schedule, morningStart: e.target.value },
                                      },
                                    })
                                  }
                                  className="px-2 py-1 border border-border bg-background rounded-lg text-xs"
                                />
                                <span className="text-muted-foreground">às</span>
                                <input
                                  type="time"
                                  value={schedule.morningEnd || '12:00'}
                                  onChange={(e) =>
                                    setSettings({
                                      ...settings,
                                      availability: {
                                        ...settings.availability,
                                        [day]: { ...schedule, morningEnd: e.target.value },
                                      },
                                    })
                                  }
                                  className="px-2 py-1 border border-border bg-background rounded-lg text-xs"
                                />

                                <span className="text-muted-foreground ml-2">Tarde:</span>
                                <input
                                  type="time"
                                  value={schedule.afternoonStart || '13:30'}
                                  onChange={(e) =>
                                    setSettings({
                                      ...settings,
                                      availability: {
                                        ...settings.availability,
                                        [day]: { ...schedule, afternoonStart: e.target.value },
                                      },
                                    })
                                  }
                                  className="px-2 py-1 border border-border bg-background rounded-lg text-xs"
                                />
                                <span className="text-muted-foreground">às</span>
                                <input
                                  type="time"
                                  value={schedule.afternoonEnd || '18:00'}
                                  onChange={(e) =>
                                    setSettings({
                                      ...settings,
                                      availability: {
                                        ...settings.availability,
                                        [day]: { ...schedule, afternoonEnd: e.target.value },
                                      },
                                    })
                                  }
                                  className="px-2 py-1 border border-border bg-background rounded-lg text-xs"
                                />
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-xs text-foreground">
                                <input
                                  type="time"
                                  value={schedule.start || '08:00'}
                                  onChange={(e) =>
                                    setSettings({
                                      ...settings,
                                      availability: {
                                        ...settings.availability,
                                        [day]: { ...schedule, start: e.target.value },
                                      },
                                    })
                                  }
                                  className="px-2 py-1 border border-border bg-background rounded-lg text-xs"
                                />
                                <span className="text-muted-foreground">às</span>
                                <input
                                  type="time"
                                  value={schedule.end || '18:00'}
                                  onChange={(e) =>
                                    setSettings({
                                      ...settings,
                                      availability: {
                                        ...settings.availability,
                                        [day]: { ...schedule, end: e.target.value },
                                      },
                                    })
                                  }
                                  className="px-2 py-1 border border-border bg-background rounded-lg text-xs"
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">Sem expediente</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coluna Lateral: Notificações & Segurança */}
        <div className="space-y-6">
          <Card className="bg-card border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Bell className="h-5 w-5 text-primary" />
                Notificações
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-2.5 rounded-xl hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2.5">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">E-mail</span>
                </div>
                <input
                  type="checkbox"
                  checked={settings.notifications.email}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, email: e.target.checked },
                    })
                  }
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2.5">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Notificações Push</span>
                </div>
                <input
                  type="checkbox"
                  checked={settings.notifications.push}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, push: e.target.checked },
                    })
                  }
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2.5">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">SMS</span>
                </div>
                <input
                  type="checkbox"
                  checked={settings.notifications.sms}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, sms: e.target.checked },
                    })
                  }
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Lock className="h-5 w-5 text-primary" />
                Segurança & Senha
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">Senha Atual</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3.5 py-2.5 pr-10 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1">Nova Senha</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-3.5 py-2.5 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm shadow-sm"
                />
              </div>
              <Button
                variant="outline"
                className="w-full text-sm font-medium rounded-xl"
                onClick={() => {
                  if (currentPassword && newPassword) {
                    passwordMutation.mutate({ current: currentPassword, new: newPassword });
                  } else {
                    toast({ title: 'Preencha os campos de senha', variant: 'error' });
                  }
                }}
                disabled={passwordMutation.isPending || !currentPassword || !newPassword}
              >
                {passwordMutation.isPending ? 'Atualizando...' : 'Atualizar Senha'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
